import { Field, Label, MediaInput } from "@zendeskgarden/react-forms";
import { Body, Cell, Head, HeaderCell, HeaderRow, Row, SortableCell, Table } from "@zendeskgarden/react-tables";
import { Tag } from "@zendeskgarden/react-tags";
import { Tooltip } from "@zendeskgarden/react-tooltips";
import { ReactComponent as InfoIcon } from "@zendeskgarden/svg-icons/src/12/info-stroke.svg";
import { ReactComponent as AlertIcon } from "@zendeskgarden/svg-icons/src/16/alert-warning-stroke.svg";
import { ReactComponent as ChevronDownIcon } from "@zendeskgarden/svg-icons/src/16/chevron-down-stroke.svg";
import { ReactComponent as ChevronUpIcon } from "@zendeskgarden/svg-icons/src/16/chevron-up-stroke.svg";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../../constants/Colors";
import { MONTHS } from "../../../../constants/Months";
import { formatAbsoluteChange, formatMetricValue, formatPercentChange } from "../logic/formatting";
import { hasDifferentReforecast, isPropertyOutlier, propertyOutlierMetrics } from "../logic/outlierLogic";
import { nextSortState, SortField, SortState } from "../logic/sortLogic";
import { formatMonthLabel, METRIC_KEYS, METRIC_LABELS, MetricKey, MonthSelection, PropertyDataHealthRow, showsAbsoluteChange, Thresholds } from "../logic/types";
import * as css from "../styles/dataHealthTable.module.scss";

interface DataHealthTableProps {
    rows: PropertyDataHealthRow[];
    thresholds: Thresholds;
    onThresholdChange: (metric: MetricKey, kind: "increase" | "decrease", value: number | null) => void;
    baseMonth: MonthSelection;
    comparisonMonth: MonthSelection;
    sortState: SortState;
    onSortChange: (next: SortState) => void;
}

interface InfoTooltipProps {
    content: string;
    className?: string;
}

interface SortableHeaderProps {
    className?: string;
    rowSpan?: number;
    label: string;
    field: SortField;
    sortState: SortState;
    onSort: (field: SortField) => () => void;
    align?: "left" | "right";
    tooltip?: string;
}

interface SubHeadersProps {
    metric: MetricKey;
    baseLabel: string;
    cmpLabel: string;
    sortState: SortState;
    onSort: (field: SortField) => () => void;
}

interface MetricCellsProps {
    metric: MetricKey;
    base: number | null | undefined;
    comparison: number | null | undefined;
    absoluteChange: number | null | undefined;
    percentChange: number | null | undefined;
    flagged: boolean;
}

interface ThresholdInputProps {
    metric: MetricKey;
    kind: "increase" | "decrease";
    labelText: string;
    helpText: string;
    icon: ReactElement;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

interface MetricThresholdCellProps {
    metric: MetricKey;
    colSpan: number;
    increase: number | null;
    decrease: number | null;
    onChange: (metric: MetricKey, kind: "increase" | "decrease", value: number | null) => void;
}

const DELTA_HELP = "Absolute change: Comparison value minus Base value.";
const DELTA_PCT_HELP = "Percent change: (Comparison − Base) / Base × 100. Used to detect outliers against your thresholds.";
const INCREASE_HELP = "Flag when this metric goes UP by more than this %";
const DECREASE_HELP = "Flag when this metric goes DOWN by more than this %";
const THRESHOLD_COMMIT_DEBOUNCE_MS = 250;

const TOOLTIP_PORTAL: Element | undefined =
    typeof document !== "undefined" ? document.body : undefined;

function differentReforecastHelp(row: PropertyDataHealthRow): string {
    return `Reforecast starts in ${MONTHS[row.reforecastStartMonthIndex]} ${row.reforecastYear}`;
}

function metricSubColumnCount(metric: MetricKey): number {
    return showsAbsoluteChange(metric) ? 4 : 3;
}

function thresholdToString(v: number | null): string {
    return v == null ? "" : String(v);
}

function parseThreshold(raw: string): number | null | undefined {
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function InfoTooltip(props: InfoTooltipProps): ReactElement {
    return (
        <Tooltip content={props.content} placement="top" size="medium" zIndex={200} appendToNode={TOOLTIP_PORTAL}>
            <span className={props.className} tabIndex={0} aria-label={props.content}>
                <InfoIcon aria-hidden />
            </span>
        </Tooltip>
    );
}

function SortableHeader(props: SortableHeaderProps): ReactElement {
    const isActive = props.sortState?.field === props.field;
    const sort = isActive ? props.sortState?.direction === "asc" ? "asc" : "desc" : undefined;

    const label = props.tooltip
        ? <Tooltip content={props.tooltip} placement="top" size="medium" zIndex={110} appendToNode={TOOLTIP_PORTAL}><span>{props.label}</span></Tooltip>
        : props.label;

    return (
        <SortableCell
            cellProps={{className: props.className, rowSpan: props.rowSpan}}
            onClick={props.onSort(props.field)}
            sort={sort}
        >
            {label}
        </SortableCell>
    );
}

function SubHeaders(props: SubHeadersProps): ReactElement {
    return (
        <>
            <SortableHeader
                label={props.baseLabel}
                field={`${props.metric}.base`}
                sortState={props.sortState}
                onSort={props.onSort}
            />
            <SortableHeader
                label={props.cmpLabel}
                field={`${props.metric}.comparison`}
                sortState={props.sortState}
                onSort={props.onSort}
            />
            {showsAbsoluteChange(props.metric) && (
                <SortableHeader
                    label="Change"
                    field={`${props.metric}.absoluteChange`}
                    sortState={props.sortState}
                    onSort={props.onSort}
                    tooltip={DELTA_HELP}
                />
            )}
            <SortableHeader
                label="Change %"
                field={`${props.metric}.percentChange`}
                sortState={props.sortState}
                onSort={props.onSort}
                tooltip={DELTA_PCT_HELP}
            />
        </>
    );
}

function MetricCells(props: MetricCellsProps): ReactElement {
    const naClass = (v: number | null | undefined) => v == null ? css.muted : undefined;
    const signClass = (v: number | null | undefined) => {
        if (v == null || v === 0) return undefined;
        return v > 0 ? css.positive : css.negative;
    };
    const flagClass = props.flagged ? css.flagged : undefined;
    const join = (...classes: (string | undefined)[]) => classes.filter(Boolean).join(" ") || undefined;

    return (
        <>
            <Cell className={naClass(props.base)}>{formatMetricValue(props.base, props.metric)}</Cell>
            <Cell className={naClass(props.comparison)}>{formatMetricValue(props.comparison, props.metric)}</Cell>
            {showsAbsoluteChange(props.metric) && (
                <Cell className={join(naClass(props.absoluteChange), signClass(props.absoluteChange))}>
                    {formatAbsoluteChange(props.absoluteChange, props.metric)}
                </Cell>
            )}
            <Cell className={join(naClass(props.percentChange), signClass(props.percentChange), flagClass)}>
                {formatPercentChange(props.percentChange)}
            </Cell>
        </>
    );
}

function ThresholdInput(props: ThresholdInputProps): ReactElement {
    return (
        <Field className={css.thresholdField}>
            <Label className={css.thresholdLabel}>
                {props.labelText}
                <InfoTooltip content={props.helpText} className={css.thresholdInfo} />
            </Label>
            <MediaInput
                isCompact
                type="number"
                step="0.1"
                placeholder="—"
                start={props.icon}
                end={<span className={css.thresholdPercent}>%</span>}
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                aria-label={`${METRIC_LABELS[props.metric]} ${props.kind} threshold`}
            />
        </Field>
    );
}

function MetricThresholdCell(props: MetricThresholdCellProps): ReactElement {
    const [localIncrease, setLocalIncrease] = useState<string>(() => thresholdToString(props.increase));
    const [localDecrease, setLocalDecrease] = useState<string>(() => thresholdToString(props.decrease));

    useEffect(() => { setLocalIncrease(thresholdToString(props.increase)); }, [props.increase]);
    useEffect(() => { setLocalDecrease(thresholdToString(props.decrease)); }, [props.decrease]);

    const increaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const decreaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onChange = props.onChange;
    const metric = props.metric;

    const commit = useCallback((kind: "increase" | "decrease", raw: string) => {
        const parsed = parseThreshold(raw);
        if (parsed === undefined) return;
        onChange(metric, kind, parsed);
    }, [onChange, metric]);

    const scheduleCommit = useCallback((kind: "increase" | "decrease", raw: string) => {
        const ref = kind === "increase" ? increaseTimeoutRef : decreaseTimeoutRef;
        if (ref.current) clearTimeout(ref.current);
        ref.current = setTimeout(() => commit(kind, raw), THRESHOLD_COMMIT_DEBOUNCE_MS);
    }, [commit]);

    useEffect(() => () => {
        if (increaseTimeoutRef.current) clearTimeout(increaseTimeoutRef.current);
        if (decreaseTimeoutRef.current) clearTimeout(decreaseTimeoutRef.current);
    }, []);

    const handleChange = (kind: "increase" | "decrease") => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (kind === "increase") setLocalIncrease(value); else setLocalDecrease(value);
        scheduleCommit(kind, value);
    };

    const handleBlur = (kind: "increase" | "decrease") => (e: React.FocusEvent<HTMLInputElement>) => {
        const ref = kind === "increase" ? increaseTimeoutRef : decreaseTimeoutRef;
        if (ref.current) {
            clearTimeout(ref.current);
            ref.current = null;
            commit(kind, e.target.value);
        }
    };

    return (
        <HeaderCell colSpan={props.colSpan}>
            <div className={css.thresholdInputs}>
                <ThresholdInput
                    metric={props.metric}
                    kind="increase"
                    labelText="Increase"
                    helpText={INCREASE_HELP}
                    icon={<ChevronUpIcon aria-hidden />}
                    value={localIncrease}
                    onChange={handleChange("increase")}
                    onBlur={handleBlur("increase")}
                />
                <ThresholdInput
                    metric={props.metric}
                    kind="decrease"
                    labelText="Decrease"
                    helpText={DECREASE_HELP}
                    icon={<ChevronDownIcon aria-hidden />}
                    value={localDecrease}
                    onChange={handleChange("decrease")}
                    onBlur={handleBlur("decrease")}
                />
            </div>
        </HeaderCell>
    );
}

export function DataHealthTable(props: DataHealthTableProps): ReactElement {
    const sortedRows = props.rows;
    const baseLabel = formatMonthLabel(props.baseMonth);
    const cmpLabel = formatMonthLabel(props.comparisonMonth);
    const totalCols = 1 + METRIC_KEYS.reduce((n, m) => n + metricSubColumnCount(m), 0);

    const handleSort = (field: SortField) => () => {
        props.onSortChange(nextSortState(props.sortState, field));
    };

    return (
        <div className={css.tableWrap}>
            <Table>
                <Head>
                    <HeaderRow>
                        <SortableHeader
                            className={`${css.propertyCol} ${css.propertyHeader}`}
                            rowSpan={3}
                            label="Property"
                            field="property"
                            sortState={props.sortState}
                            onSort={handleSort}
                            align="left"
                        />
                        {METRIC_KEYS.map(metric => (
                            <HeaderCell key={metric} colSpan={metricSubColumnCount(metric)}>
                                {METRIC_LABELS[metric]}
                            </HeaderCell>
                        ))}
                    </HeaderRow>
                    <HeaderRow className={css.thresholdRow}>
                        {METRIC_KEYS.map(metric => (
                            <MetricThresholdCell
                                key={metric}
                                metric={metric}
                                colSpan={metricSubColumnCount(metric)}
                                increase={props.thresholds[metric].increase}
                                decrease={props.thresholds[metric].decrease}
                                onChange={props.onThresholdChange}
                            />
                        ))}
                    </HeaderRow>
                    <HeaderRow>
                        {METRIC_KEYS.map(metric => (
                            <SubHeaders
                                key={metric}
                                metric={metric}
                                baseLabel={baseLabel}
                                cmpLabel={cmpLabel}
                                sortState={props.sortState}
                                onSort={handleSort}
                            />
                        ))}
                    </HeaderRow>
                </Head>
                <Body>
                    {sortedRows.length === 0 && (
                        <Row>
                            <Cell colSpan={totalCols} className={css.emptyRow}>
                                No properties match your filters.
                            </Cell>
                        </Row>
                    )}
                    {sortedRows.map(row => {
                        const differentReforecast = hasDifferentReforecast(row, props.baseMonth);
                        const flaggedList = propertyOutlierMetrics(row, props.thresholds);
                        const outlier = isPropertyOutlier(row, props.thresholds);
                        const flagged = new Set<MetricKey>(flaggedList);
                        const badgeTooltip = flaggedList.length === 0
                            ? ""
                            : `Flagged metric${flaggedList.length > 1 ? "s" : ""}: ${flaggedList.map(m => {
                                return `${METRIC_LABELS[m]} (${formatPercentChange(row[m].percentChange)})`;
                            }).join(", ")}`;
                        return (
                            <Row key={row.propertyId}>
                                <HeaderCell scope="row" className={css.propertyName}>
                                    <div className={css.propertyNameInner}>
                                        <span>{row.propertyName}</span>
                                        {differentReforecast && (
                                            <InfoTooltip content={differentReforecastHelp(row)} className={css.preReforecastInfo} />
                                        )}
                                        {outlier && (
                                            <Tooltip content={badgeTooltip} placement="top" size="medium" zIndex={110} appendToNode={TOOLTIP_PORTAL}>
                                                <Tag size="small" isPill hue={COLORS.MAGENTA_400} className={css.outlierBadge}>
                                                    <Tag.Avatar>
                                                        <AlertIcon aria-hidden />
                                                    </Tag.Avatar>
                                                    <span>Outlier</span>
                                                </Tag>
                                            </Tooltip>
                                        )}
                                    </div>
                                </HeaderCell>
                                {METRIC_KEYS.map(metric => {
                                    const m = row[metric];
                                    return (
                                        <MetricCells
                                            key={metric}
                                            metric={metric}
                                            base={m.base}
                                            comparison={m.comparison}
                                            absoluteChange={m.absoluteChange}
                                            percentChange={m.percentChange}
                                            flagged={flagged.has(metric)}
                                        />
                                    );
                                })}
                            </Row>
                        );
                    })}
                </Body>
            </Table>
        </div>
    );
}
